/**
 * 「LINKS TO THIS PAGE」空态彩蛋：没有任何页面链接到这张卡时，底部显示一个 emoji + 一句英文短文案。
 *
 * 选取规则（优先级从高到低，命中即返回）：
 *   1. 节日（本地日期）——万圣节 / 圣诞 / 跨年 / 愚人节 / 春节 / 中秋，整站统一换成节日款
 *   2. 深夜（本地 0–4 点）
 *   3. 稀有款：cardId 哈希 % 64 === 0
 *   4. 基础池：cardId 哈希取模，同一张卡每次打开都一样（像它自己的吉祥物）
 *
 * 纯函数、无 React 依赖，`node:test` 直接测（orphan-egg.test.js）。
 * BASE_EGGS 的顺序就是哈希映射，之后只在末尾追加，别重排，否则所有卡片的吉祥物会集体换一遍。
 */

/** fnv1a 32 位字符串哈希，返回非负整数 */
export function hashStr(s) {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

export const BASE_EGGS = [
  { key: 'ghost', emoji: '👻', text: 'Nobody links here yet. Spooky.' },
  { key: 'island', emoji: '🏝️', text: 'An island. No bridges lead here.' },
  { key: 'sprout', emoji: '🌱', text: 'A fresh sprout. No roots yet.' },
  { key: 'compass', emoji: '🧭', text: 'Uncharted. You found it without a map.' },
  { key: 'planet', emoji: '🪐', text: 'A lone planet with no moons.' },
  { key: 'candle', emoji: '🕯️', text: 'Quiet here. Just you and the page.' },
  { key: 'chick', emoji: '🐣', text: 'Just hatched. Nobody has mentioned it yet.' },
  { key: 'balloon', emoji: '🎈', text: 'Floating free, tethered to nothing.' },
  { key: 'puzzle', emoji: '🧩', text: 'A piece still waiting to fit somewhere.' },
  { key: 'moon', emoji: '🌙', text: 'Orbiting alone for now.' },
  { key: 'leaf', emoji: '🍃', text: 'Drifted here on its own.' },
  { key: 'telescope', emoji: '🔭', text: 'Off the map. Nice find.' },
  { key: 'thread', emoji: '🧵', text: 'No threads lead here. Yet.' },
  { key: 'shell', emoji: '🐚', text: 'A shell with no ocean nearby.' },
  { key: 'antenna', emoji: '📡', text: 'No signal from other pages.' },
  { key: 'key', emoji: '🗝️', text: 'You got here without a key.' },
  { key: 'tent', emoji: '⛺', text: 'Camping alone at the edge of the graph.' },
  { key: 'bubble', emoji: '🫧', text: 'A bubble nobody has popped.' },
]

export const RARE_EGG = { key: 'rare', emoji: '✨', text: 'You found a rare one. Nobody links here, but you did.' }
export const NIGHT_EGG = { key: 'owl', emoji: '🦉', text: 'Late night browsing. The owl approves.' }

/** 农历节日按年写死（初一 / 中秋），'MM-DD'；到 2030 年以后要续表 */
const LUNAR_NEW_YEAR = { 2026: '02-17', 2027: '02-06', 2028: '01-26', 2029: '02-13', 2030: '02-03' }
const MID_AUTUMN = { 2026: '09-25', 2027: '09-15', 2028: '10-03', 2029: '09-22', 2030: '09-12' }

const HOLIDAY_EGGS = [
  { dates: ['10-31'], egg: { key: 'halloween', emoji: '🎃', text: 'Ghost season. Fitting that nobody links here.' } },
  { dates: ['12-24', '12-25'], egg: { key: 'xmas', emoji: '🎄', text: 'A quiet corner under the tree.' } },
  { dates: ['12-31', '01-01'], egg: { key: 'newyear', emoji: '🎆', text: 'Fresh year, fresh page. Links to come.' } },
  { dates: ['04-01'], egg: { key: 'aprilfools', emoji: '🃏', text: 'This page has 9,999 backlinks. Just kidding.' } },
  { table: LUNAR_NEW_YEAR, egg: { key: 'lunar', emoji: '🧧', text: 'Happy new year. Even orphans get red envelopes.' } },
  { table: MID_AUTUMN, egg: { key: 'midautumn', emoji: '🥮', text: 'Full moon, empty inbox.' } },
]

const pad2 = (n) => (n < 10 ? '0' + n : String(n))

/** 本地日期 → 'MM-DD'（别走 new Date('YYYY-MM-DD')，那是 UTC 解析会差一天） */
function monthDay(d) {
  return pad2(d.getMonth() + 1) + '-' + pad2(d.getDate())
}

export function holidayEgg(now) {
  const md = monthDay(now)
  const y = now.getFullYear()
  for (const h of HOLIDAY_EGGS) {
    if (h.dates ? h.dates.includes(md) : h.table[y] === md) return h.egg
  }
  return null
}

/**
 * @param {string} cardId 卡片 id（空/非字符串按 '' 处理，稳定回落，不抛错）
 * @param {Date} now 本地时间，默认现在；测试用来钉节日 / 深夜
 * @returns {{ key: string, emoji: string, text: string }}
 */
export function pickOrphanEgg(cardId, now = new Date()) {
  const holiday = holidayEgg(now)
  if (holiday) return holiday
  const hour = now.getHours()
  if (hour >= 0 && hour < 5) return NIGHT_EGG
  const h = hashStr(typeof cardId === 'string' ? cardId : '')
  if (h % 64 === 0) return RARE_EGG
  return BASE_EGGS[h % BASE_EGGS.length]
}
